import React, { useEffect, useMemo, useState } from "react";
import { AlertCircle, Save, X } from "lucide-react";
import API from "../../../util/API";
import { voucherService } from "./voucherService";

const toDateTimeLocal = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

const parseIdList = (value) =>
  Array.from(
    new Set(
      String(value || "")
        .split(/[\s,]+/)
        .map((entry) => entry.trim())
        .filter(Boolean)
    )
  );

const createInitialForm = (voucher) => {
  const conditions = voucher?.conditions || {};
  return {
    code: voucher?.code || "",
    type: voucher?.type || "fixed",
    value: voucher?.value ?? 0,
    currency: String(voucher?.currency || "USD").toUpperCase(),
    currencyMode: voucher?.type === "fixed" ? "single" : voucher?.currencyMode || "single",
    maxDiscount: voucher?.maxDiscount ?? null,
    startDate: toDateTimeLocal(voucher?.startDate),
    endDate: toDateTimeLocal(voucher?.endDate),
    isActive: voucher?.isActive !== false,
    conditions: {
      ...conditions,
      minSpend: conditions.minSpend ?? 0,
      firstTimeUser: conditions.firstTimeUser === true,
      validCountries: Array.isArray(conditions.validCountries) ? conditions.validCountries : [],
      productIds: Array.isArray(conditions.productIds) ? conditions.productIds : [],
      requiredProductIds: Array.isArray(conditions.requiredProductIds)
        ? conditions.requiredProductIds
        : [],
      usagePerUser: conditions.usagePerUser ?? 1,
      totalUsageLimit: conditions.totalUsageLimit ?? null,
    },
    profitRules: voucher?.profitRules || {
      minProfit: 0,
      platformSharePercent: 100,
      vendorSharePercent: 0,
    },
    marketing: voucher?.marketing || {},
    triggers: voucher?.triggers || {},
    rules: Array.isArray(voucher?.rules) ? voucher.rules : [],
  };
};

export default function VoucherForm({ voucher = null, onClose, onSave }) {
  const [formData, setFormData] = useState(() => createInitialForm(voucher));
  const [allCountries, setAllCountries] = useState(
    !Array.isArray(voucher?.conditions?.validCountries) || !voucher.conditions.validCountries.length
  );
  const [eligibleProductIds, setEligibleProductIds] = useState(
    (voucher?.conditions?.productIds || []).join("\n")
  );
  const [requiredProductIds, setRequiredProductIds] = useState(
    (voucher?.conditions?.requiredProductIds || []).join("\n")
  );
  const [countries, setCountries] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    API.get("/settings/countries")
      .then((response) => {
        if (!active) return;
        const list = Array.isArray(response?.data?.countries) ? response.data.countries : [];
        setCountries(
          list
            .map((country) => ({
              code: String(country?.countryCode || "").trim().toUpperCase(),
              currency: String(country?.currencyCode || "").trim().toUpperCase(),
              flag: country?.flagEmoji || "",
              name: country?.countryName || country?.countryCode || "",
            }))
            .filter((country) => country.code && country.currency)
            .sort((left, right) => left.name.localeCompare(right.name))
        );
      })
      .catch(() => {
        if (active) setCountries([]);
      })
      .finally(() => {
        if (active) setCountriesLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const currencyOptions = useMemo(
    () => Array.from(new Set(["USD", formData.currency, ...countries.map((country) => country.currency)])).sort(),
    [countries, formData.currency]
  );

  const setField = (name, value) => {
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const setCondition = (name, value) => {
    setFormData((current) => ({
      ...current,
      conditions: { ...current.conditions, [name]: value },
    }));
  };

  const handleTypeChange = (type) => {
    setFormData((current) => ({
      ...current,
      type,
      value: type === "free_shipping" ? 0 : current.value,
      maxDiscount: type === "percent" ? current.maxDiscount : null,
      currencyMode: type === "fixed" ? "single" : current.currencyMode,
    }));
  };

  const handleCurrencyModeChange = (currencyMode) => {
    setFormData((current) => ({
      ...current,
      currencyMode,
      maxDiscount: currencyMode === "any" ? null : current.maxDiscount,
      conditions: {
        ...current.conditions,
        minSpend: currencyMode === "any" ? 0 : current.conditions.minSpend,
      },
    }));
  };

  const toggleCountry = (countryCode) => {
    const selected = new Set(formData.conditions.validCountries || []);
    if (selected.has(countryCode)) selected.delete(countryCode);
    else selected.add(countryCode);
    setCondition("validCountries", Array.from(selected));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      const code = String(formData.code || "").trim().toUpperCase();
      if (!code) throw new Error("Voucher code is required.");
      if (formData.type !== "free_shipping" && Number(formData.value) <= 0) {
        throw new Error("Voucher value must be greater than zero.");
      }
      if (formData.type === "percent" && Number(formData.value) > 100) {
        throw new Error("Percentage discount cannot exceed 100%.");
      }
      if (!formData.startDate || !formData.endDate) {
        throw new Error("Start and end dates are required.");
      }
      if (new Date(formData.endDate) <= new Date(formData.startDate)) {
        throw new Error("End date must be after start date.");
      }
      if (!allCountries && !formData.conditions.validCountries.length) {
        throw new Error("Select at least one shipping country or choose all enabled countries.");
      }

      const submitData = {
        ...formData,
        code,
        value: formData.type === "free_shipping" ? 0 : Number(formData.value),
        currency: String(formData.currency || "USD").toUpperCase(),
        currencyMode: formData.type === "fixed" ? "single" : formData.currencyMode,
        maxDiscount:
          formData.type === "percent" && formData.maxDiscount
            ? Number(formData.maxDiscount)
            : null,
        conditions: {
          ...formData.conditions,
          minSpend: Number(formData.conditions.minSpend || 0),
          validCountries: allCountries ? [] : formData.conditions.validCountries,
          productIds: parseIdList(eligibleProductIds),
          requiredProductIds: parseIdList(requiredProductIds),
          totalUsageLimit: formData.conditions.totalUsageLimit
            ? Number.parseInt(formData.conditions.totalUsageLimit, 10)
            : null,
          usagePerUser: Number.parseInt(formData.conditions.usagePerUser || 1, 10),
        },
      };

      if (voucher) {
        const updateData = { ...submitData };
        delete updateData.code;
        await voucherService.updateVoucher(voucher._id, updateData);
      } else {
        await voucherService.createVoucher(submitData);
      }
      onSave();
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          requestError?.message ||
          "Failed to save voucher."
      );
    } finally {
      setLoading(false);
    }
  };

  const amountLabel = formData.type === "percent" ? "Discount percentage" : "Discount amount";
  const monetaryFieldsDisabled = formData.currencyMode === "any";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3">
      <div className="flex max-h-[94vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <div>
            <h2 className="text-base font-semibold text-slate-900">
              {voucher ? "Edit voucher" : "Create voucher"}
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">
              Voucher amounts stay in their configured currency and are never converted.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50"
            title="Close"
          >
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto p-5">
          <div className="space-y-5">
            {error && (
              <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <section>
              <h3 className="mb-2 text-sm font-semibold text-slate-900">Offer</h3>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <label className="text-xs font-medium text-slate-700">
                  Voucher code
                  <input
                    value={formData.code}
                    onChange={(event) => setField("code", event.target.value.toUpperCase())}
                    disabled={Boolean(voucher)}
                    placeholder="WELCOME10"
                    className="mt-1 h-9 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-500 disabled:bg-slate-100"
                  />
                </label>
                <label className="text-xs font-medium text-slate-700">
                  Discount type
                  <select
                    value={formData.type}
                    onChange={(event) => handleTypeChange(event.target.value)}
                    className="mt-1 h-9 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-500"
                  >
                    <option value="fixed">Fixed amount</option>
                    <option value="percent">Percentage</option>
                    <option value="free_shipping">Free shipping</option>
                  </select>
                </label>
                {formData.type !== "free_shipping" && (
                  <label className="text-xs font-medium text-slate-700">
                    {amountLabel}
                    <input
                      type="number"
                      min="0.01"
                      max={formData.type === "percent" ? "100" : undefined}
                      step="0.01"
                      value={formData.value}
                      onChange={(event) => setField("value", event.target.value)}
                      className="mt-1 h-9 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-500"
                    />
                  </label>
                )}
                {formData.type !== "fixed" && (
                  <label className="text-xs font-medium text-slate-700">
                    Checkout currencies
                    <select
                      value={formData.currencyMode}
                      onChange={(event) => handleCurrencyModeChange(event.target.value)}
                      className="mt-1 h-9 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-500"
                    >
                      <option value="single">One currency</option>
                      <option value="any">Any currency</option>
                    </select>
                  </label>
                )}
                {formData.currencyMode === "single" && (
                  <label className="text-xs font-medium text-slate-700">
                    Voucher currency
                    <select
                      value={formData.currency}
                      onChange={(event) => setField("currency", event.target.value)}
                      className="mt-1 h-9 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-500"
                    >
                      {currencyOptions.map((currency) => (
                        <option key={currency} value={currency}>{currency}</option>
                      ))}
                    </select>
                  </label>
                )}
                {formData.type === "percent" && formData.currencyMode === "single" && (
                  <label className="text-xs font-medium text-slate-700">
                    Maximum discount ({formData.currency})
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.maxDiscount || ""}
                      onChange={(event) => setField("maxDiscount", event.target.value || null)}
                      placeholder="No cap"
                      className="mt-1 h-9 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-500"
                    />
                  </label>
                )}
              </div>
            </section>

            <section className="border-t border-slate-200 pt-4">
              <div className="mb-2 flex items-center justify-between gap-3">
                <h3 className="text-sm font-semibold text-slate-900">Shipping countries</h3>
                <label className="flex items-center gap-2 text-xs font-medium text-slate-700">
                  <input
                    type="checkbox"
                    checked={allCountries}
                    onChange={(event) => setAllCountries(event.target.checked)}
                  />
                  All enabled countries
                </label>
              </div>
              {!allCountries && (
                <div className="grid max-h-36 grid-cols-2 gap-1 overflow-y-auto rounded-md border border-slate-200 p-2 md:grid-cols-3">
                  {countriesLoading ? (
                    <p className="col-span-full p-2 text-xs text-slate-500">Loading countries...</p>
                  ) : countries.length ? (
                    countries.map((country) => (
                      <label key={country.code} className="flex items-center gap-2 rounded px-2 py-1.5 text-xs hover:bg-slate-50">
                        <input
                          type="checkbox"
                          checked={formData.conditions.validCountries.includes(country.code)}
                          onChange={() => toggleCountry(country.code)}
                        />
                        <span>{country.flag}</span>
                        <span className="truncate">{country.name}</span>
                        <span className="ml-auto text-slate-400">{country.currency}</span>
                      </label>
                    ))
                  ) : (
                    <p className="col-span-full p-2 text-xs text-red-600">No enabled countries were loaded.</p>
                  )}
                </div>
              )}
            </section>

            <section className="border-t border-slate-200 pt-4">
              <h3 className="mb-2 text-sm font-semibold text-slate-900">Item eligibility</h3>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <label className="text-xs font-medium text-slate-700">
                  Eligible product IDs
                  <textarea
                    rows={3}
                    value={eligibleProductIds}
                    onChange={(event) => setEligibleProductIds(event.target.value)}
                    placeholder="Leave empty for all products"
                    className="mt-1 w-full resize-y rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                  />
                </label>
                <label className="text-xs font-medium text-slate-700">
                  Required product IDs
                  <textarea
                    rows={3}
                    value={requiredProductIds}
                    onChange={(event) => setRequiredProductIds(event.target.value)}
                    placeholder="Optional cart requirements"
                    className="mt-1 w-full resize-y rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500"
                  />
                </label>
              </div>
            </section>

            <section className="border-t border-slate-200 pt-4">
              <h3 className="mb-2 text-sm font-semibold text-slate-900">Limits and schedule</h3>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                <label className="text-xs font-medium text-slate-700">
                  Minimum eligible spend{formData.currencyMode === "single" ? ` (${formData.currency})` : ""}
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    disabled={monetaryFieldsDisabled}
                    value={formData.conditions.minSpend}
                    onChange={(event) => setCondition("minSpend", event.target.value)}
                    className="mt-1 h-9 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-500 disabled:bg-slate-100"
                  />
                </label>
                <label className="text-xs font-medium text-slate-700">
                  Uses per customer
                  <input
                    type="number"
                    min="1"
                    value={formData.conditions.usagePerUser}
                    onChange={(event) => setCondition("usagePerUser", event.target.value)}
                    className="mt-1 h-9 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-500"
                  />
                </label>
                <label className="text-xs font-medium text-slate-700">
                  Total usage limit
                  <input
                    type="number"
                    min="1"
                    value={formData.conditions.totalUsageLimit || ""}
                    onChange={(event) => setCondition("totalUsageLimit", event.target.value || null)}
                    placeholder="Unlimited"
                    className="mt-1 h-9 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-500"
                  />
                </label>
                <label className="text-xs font-medium text-slate-700">
                  Starts
                  <input
                    type="datetime-local"
                    value={formData.startDate}
                    onChange={(event) => setField("startDate", event.target.value)}
                    className="mt-1 h-9 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-500"
                  />
                </label>
                <label className="text-xs font-medium text-slate-700">
                  Ends
                  <input
                    type="datetime-local"
                    value={formData.endDate}
                    onChange={(event) => setField("endDate", event.target.value)}
                    className="mt-1 h-9 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-500"
                  />
                </label>
                <div className="flex flex-col justify-end gap-2 pb-1 text-xs font-medium text-slate-700">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.conditions.firstTimeUser}
                      onChange={(event) => setCondition("firstTimeUser", event.target.checked)}
                    />
                    First paid order only
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(event) => setField("isActive", event.target.checked)}
                    />
                    Active
                  </label>
                </div>
              </div>
            </section>
          </div>

          <div className="sticky bottom-0 mt-5 flex justify-end gap-2 border-t border-slate-200 bg-white pt-4">
            <button
              type="button"
              onClick={onClose}
              className="h-9 rounded-md border border-slate-300 px-4 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || countriesLoading}
              className="flex h-9 items-center gap-2 rounded-md bg-slate-900 px-4 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
            >
              <Save size={15} />
              {loading ? "Saving..." : voucher ? "Update voucher" : "Create voucher"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
